<template>
	<div class="help-panel-wrapper">
		<v-btn
			v-if="!hideButton"
			text
			small
			color="primary"
			:aria-label="$t('help.openAria')"
			class="help-panel-button"
			@click="openHelp"
		>
			<v-icon left small>mdi-help-circle-outline</v-icon>
			{{ $t('help.openLabel') }}
		</v-btn>

		<!-- Overlay con CSS fijo. No depende de v-app ni de v-navigation-drawer. -->
		<transition name="help-panel-fade">
			<div
				v-if="open"
				class="help-panel-backdrop"
				@click="close"
			></div>
		</transition>

		<transition name="help-panel-slide">
			<aside
				v-if="open"
				class="help-panel-overlay"
				role="dialog"
				aria-modal="true"
			>
				<div class="help-panel-overlay__header">
					<div class="help-panel-overlay__heading">
						<v-icon color="primary" size="22">mdi-information-outline</v-icon>
						<h2>{{ title }}</h2>
					</div>
					<v-btn icon @click="close" :aria-label="$t('help.close')">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</div>
				<v-divider />
				<div class="help-panel-overlay__body">
					<slot />
					<a
						v-if="fullGuideUrl"
						class="help-panel-full-guide"
						:href="fullGuideUrl"
						target="_blank"
						rel="noopener noreferrer"
					>
						{{ $t('help.viewFullGuide') }}
						<v-icon small color="primary">mdi-open-in-new</v-icon>
					</a>
				</div>
			</aside>
		</transition>
	</div>
</template>

<script>
import { getRuntimeConfig } from "../../utils/runtime-config";
import { isHttpUrl, joinDocsUrl } from "../../utils/docs-link";

export default {
	name: "HelpPanel",
	props: {
		title: {
			type: String,
			required: true,
		},
		hideButton: {
			type: Boolean,
			default: false,
		},
		// Ruta relativa de la guía en el sitio de docs (p.ej. "administracion/vacaciones").
		// Solo las páginas MVP la cablean; sin ella no se muestra "Ver guía completa".
		docPath: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			open: false,
		};
	},
	computed: {
		// URL completa a la guía, o null si falta docPath o el tenant no activó docsUrl
		// (o no es http(s), guarda defensiva). El enlace se muestra solo cuando no es null.
		fullGuideUrl() {
			if (!this.docPath) return null;
			const cfg = getRuntimeConfig();
			const base = cfg && cfg.brand ? cfg.brand.docsUrl : undefined;
			return isHttpUrl(base) ? joinDocsUrl(base, this.docPath) : null;
		},
	},
	watch: {
		open(val) {
			if (typeof document !== "undefined") {
				document.body.style.overflow = val ? "hidden" : "";
			}
		},
	},
	beforeDestroy() {
		if (typeof document !== "undefined") {
			document.body.style.overflow = "";
		}
	},
	methods: {
		openHelp() {
			this.open = true;
		},
		close() {
			this.open = false;
		},
	},
};
</script>

<style scoped>
.help-panel-wrapper {
	display: inline-flex;
}

.help-panel-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.45);
	z-index: 1100;
}

.help-panel-overlay {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: 540px;
	max-width: 92vw;
	background: #ffffff;
	box-shadow: -8px 0 24px rgba(15, 23, 42, 0.12);
	display: flex;
	flex-direction: column;
	z-index: 1101;
}

.help-panel-overlay__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px;
	background: #fafafa;
	flex: 0 0 auto;
}

.help-panel-overlay__heading {
	display: flex;
	align-items: center;
	gap: 8px;
}

.help-panel-overlay__heading h2 {
	margin: 0;
	font-size: 1.15rem;
	font-weight: 600;
	color: #263238;
}

.help-panel-overlay__body {
	padding: 20px;
	overflow-y: auto;
	flex: 1 1 auto;
	color: #455a64;
	line-height: 1.55;
	font-size: 0.94rem;
}

.help-panel-overlay__body ::v-deep h3 {
	font-size: 0.95rem;
	font-weight: 600;
	color: #263238;
	margin: 20px 0 8px 0;
	letter-spacing: 0.2px;
}

.help-panel-overlay__body ::v-deep h3:first-child {
	margin-top: 0;
}

.help-panel-overlay__body ::v-deep p {
	margin: 0 0 10px 0;
}

.help-panel-overlay__body ::v-deep ul,
.help-panel-overlay__body ::v-deep ol {
	margin: 0 0 12px 0;
	padding-left: 20px;
}

.help-panel-overlay__body ::v-deep li {
	margin-bottom: 4px;
}

.help-panel-overlay__body ::v-deep code {
	background: #eceff1;
	padding: 2px 6px;
	border-radius: 3px;
	font-size: 0.88em;
	color: #37474f;
}

.help-panel-overlay__body ::v-deep pre {
	background: #eceff1;
	padding: 12px 14px;
	border-radius: 6px;
	overflow-x: auto;
	font-size: 0.85em;
	color: #37474f;
	border: 1px solid #e0e0e0;
	margin-bottom: 12px;
}

.help-panel-overlay__body ::v-deep .help-tip {
	background: #e3f2fd;
	border-left: 3px solid #1976d2;
	padding: 10px 12px;
	border-radius: 4px;
	margin: 12px 0;
	font-size: 0.92em;
}

.help-panel-full-guide {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-top: 12px;
	font-weight: 600;
	color: var(--v-primary-base, #104782);
	text-decoration: none;
}

.help-panel-full-guide:hover {
	text-decoration: underline;
}

.help-panel-fade-enter-active,
.help-panel-fade-leave-active {
	transition: opacity 0.18s ease;
}
.help-panel-fade-enter,
.help-panel-fade-leave-to {
	opacity: 0;
}

.help-panel-slide-enter-active,
.help-panel-slide-leave-active {
	transition: transform 0.22s ease;
}
.help-panel-slide-enter,
.help-panel-slide-leave-to {
	transform: translateX(100%);
}
</style>
